import { useEffect, useState } from 'react'
import { BaseUrl, forceDark, forceLight, isProduction, useBreakpoint } from 'utils'
import { Box, Button, DateOrTime, Flex, FlexProps, Popover, Skeleton, Text } from '.'
import { CubeTransparentIcon, InformationCircleIcon } from './icons'

export interface Props extends Omit<FlexProps, 'children'> {
  action?: React.ReactNode
}

export default function DevelopmentBar({ action, ...rest }: Props) {
  const breakpoint = useBreakpoint()

  if (isProduction()) return null

  function goToProduction() {
    const { origin } = window.location
    let baseUrl = BaseUrl.web
    if (origin === BaseUrl['dev-events'] || origin === BaseUrl['local-events'])
      baseUrl = BaseUrl.events

    window.location.href = `${baseUrl}${document.location.pathname}`
  }

  return (
    <Flex
      backgroundColor="#3C4BD3"
      alignItems="center"
      justifyContent="space-between"
      px={{ base: 4, md: 6 }}
      py={2}
      {...forceDark}
      {...rest}
    >
      <Flex alignItems="center" gap={2}>
        <CubeTransparentIcon />
        <Text userSelect="none" py={1} textStyle="h6">
          Dev{breakpoint > 1 && 'elopment Mode'}
        </Text>
        <Popover
          autoFocus
          closeOnInteractOutside
          hideClose
          lazyMount
          positioning={{ placement: 'bottom-start' }}
          showArrow
          contentProps={{ width: '500px', ...forceLight }}
          trigger={<InformationCircleIcon cursor="pointer" type="outline" />}
        >
          <Information />
        </Popover>
      </Flex>
      <Flex alignItems="center" gap={2}>
        {action}
        <Button onClick={goToProduction} size="sm" backgroundColor="white" color="black">
          {breakpoint > 0 && 'Switch to '}Production
        </Button>
      </Flex>
    </Flex>
  )
}

function Information() {
  const [issues, setIssues] = useState<LinearIssue[]>(
    linearIssues?.map(id => ({ identifier: id.toUpperCase() })) || []
  )

  useEffect(() => {
    if (!linearIssues?.length) return

    Promise.all(linearIssues.map(fetchIssue)).then(issues =>
      setIssues(issues.filter(Boolean) as LinearIssue[])
    )
  }, [])

  return (
    <Flex py={4} flexDirection="column" gap={4}>
      <Flex flexDirection="column" px={4} gap={2}>
        <Text>This is the development site for new feature testing.</Text>
        {gitBranch !== 'dev' && (
          <Text>
            <strong>Git Branch:</strong> {gitBranch}
          </Text>
        )}
      </Flex>

      <Flex direction="column" gap={1}>
        <Text textStyle="h5" px={4}>
          Development
        </Text>
        <Flex px={4}>
          <Text flex={1} textStyle="bodySmall">
            Issue
          </Text>
          <Text textStyle="bodySmall">Reviewed</Text>
        </Flex>
        <Box as="ul">
          {issues?.map(({ identifier, title, completedAt }) => (
            <Box
              as="li"
              key={identifier}
              onClick={() => window.open(`https://linear.app/entevate/issue/${identifier}`)}
              px={4}
              display="flex"
              alignItems="center"
              cursor="pointer"
              textStyle="bodySmall"
              py={0.5}
              _hover={{ backgroundColor: 'gray.100' }}
            >
              <Text width="75px">{identifier}:</Text>
              {title ? (
                <Text flex={1} lineClamp={1} pr={4} fontWeight="normal">
                  {title}
                </Text>
              ) : (
                <Skeleton height="16px" width={Math.random() * 200 + 100} />
              )}
              {completedAt && <DateOrTime>{completedAt}</DateOrTime>}
            </Box>
          ))}
        </Box>
      </Flex>

      <Flex direction="column" gap={1}>
        <Text textStyle="h5" px={4}>
          Experimental
        </Text>
        <Box as="ul">
          {experimentalFeatures?.map(feature => (
            <Box as="li" key={feature} px={4} py={0.5}>
              <Text textStyle="bodySmall" fontWeight="normal">
                {feature}
              </Text>
            </Box>
          ))}
        </Box>
      </Flex>
    </Flex>
  )
}

const experimentalFeatures = [
  'Session Import',
  'Content (templates, traits, media, web sites, text)',
  'Integrations',
]

async function fetchIssue(id: string) {
  if (!linearApiKey) return console.error('VITE_LINEAR_READ_API_KEY is not set')
  const query = `{ issue(id: "${id}") { identifier title completedAt } }`

  try {
    const response = await fetch('https://api.linear.app/graphql', {
      method: 'POST',
      headers: { Authorization: linearApiKey, 'Content-Type': 'application/json' },
      body: JSON.stringify({ query }),
    })

    if (!response.ok) {
      throw new Error(`Linear API error: ${response.status}`)
    }

    const data = await response.json()
    return data.data?.issue as LinearIssue
  } catch (error) {
    console.error(`Failed to fetch issue ${id}:`, error)
  }
}

interface LinearIssue {
  identifier: string
  title?: string
  completedAt?: string | null
}

const gitBranch = import.meta.env.VITE_GIT_BRANCH_NAME
const linearIssues = import.meta.env.VITE_LINEAR_ISSUES?.split(',') as string[] | undefined
const linearApiKey = import.meta.env.VITE_LINEAR_READ_API_KEY
